/**
 * Referral code API helpers – talks to the zoolife backend.
 * Endpoints: referral-available, join, referral-links, claim
 */
import { makeAuthenticatedApiCall } from './authApi';
import { USER_API_URL, API_CONFIG } from '../config/api';

/**
 * Match API scheme (https) so reward images aren't blocked on iOS when backend returns http.
 */
export const normalizeReferralMediaUri = (uri) => {
  if (uri == null || typeof uri !== 'string') return uri;
  const u = uri.trim();
  if (!u) return u;
  const base = API_CONFIG.BASE_URL || '';
  if (base.startsWith('https://') && u.startsWith('http://')) {
    try {
      if (new URL(u).host === new URL(base).host) {
        return u.replace(/^http:\/\//i, 'https://');
      }
    } catch (_) {
      /* ignore */
    }
  }
  return u;
};

/**
 * GET referral-available options after scan (Zoolyfe Club batch QR only).
 * @returns {{ batchId: number }|null} null = skip referral offer in popup (not batch context).
 */
export const referralOptionsAfterScan = (scanUiResult) => {
  const bid = scanUiResult?.zclub_qr_batch_id;
  const n = bid != null ? Number(bid) : 0;
  return Number.isFinite(n) && n > 0 ? { batchId: n } : null;
};

/** Whether POL popup may run after scan (QR has batch id). */
export const shouldOfferPolReferralPopup = (scanUiResult) => referralOptionsAfterScan(scanUiResult) != null;

/**
 * Normalize joinable POL promos (multiple programs can overlap).
 * @param {object|null} data getReferralAvailable payload (not full API envelope)
 * @returns {Array<{ id_promosi: number, nama_promosi: string, scope_line?: string, landing_url?: string }>}
 */
export const normalizeReferralPromotions = (data) => {
  if (!data || !data.available) return [];
  if (Array.isArray(data.promotions) && data.promotions.length > 0) return data.promotions;
  if (data.id_promosi) {
    return [
      {
        id_promosi: data.id_promosi,
        nama_promosi: data.nama_promosi || '',
        scope_line: data.scope_line || '',
        landing_url: data.landing_url || '',
      },
    ];
  }
  return [];
};

/**
 * GET promotions/referral-available
 * Returns { available, promotions[], ... }; promotions = all joinable.
 * Never throws — on error returns { available: false }.
 * @param {{ batchId?: number }} [options] batchId > 0 limits to promos tied to that QR batch in admin (post-scan popup).
 */
export const getReferralAvailable = async (options = {}) => {
  try {
    let url = `${USER_API_URL}/promotions/referral-available`;
    const bid = options.batchId != null ? Number(options.batchId) : 0;
    if (Number.isFinite(bid) && bid > 0) {
      url += `?batch_id=${encodeURIComponent(String(bid))}`;
    }
    const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
    return res?.data ?? { available: false };
  } catch (e) {
    console.warn('getReferralAvailable error:', e?.message);
    return { available: false };
  }
};

/**
 * GET referral/check-code?code=XXX
 * Check if a referral code is still available.
 * Returns { available: boolean, message: string }
 */
export const checkReferralCode = async (code) => {
  try {
    const url = `${USER_API_URL}/referral/check-code?code=${encodeURIComponent(code)}`;
    const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
    return res?.data ?? { available: false, message: 'Failed to check code' };
  } catch (e) {
    return { available: false, message: e?.message || 'Failed to check code' };
  }
};

/**
 * POST promotions/{id}/join
 * Join a referral promo as POL. Server assigns the code.
 */
export const joinPromotion = async (idPromosi) => {
  const url = `${USER_API_URL}/promotions/${idPromosi}/join`;
  const res = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (res?.status !== 'success') throw new Error(res?.message || 'Failed to join');
  return res?.data ?? null;
};

/**
 * GET referral-links
 * POL's referral links with progress, conditions, and claim status.
 */
export const getReferralLinks = async () => {
  const url = `${USER_API_URL}/referral-links`;
  const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Failed to load data');
  return res?.data ?? [];
};

/**
 * POST referral/claim
 * Body: { referral_link_id, condition_index?, id_alamat? }
 * Claim the reward for a specific condition (0-based index).
 * @param {{ idAlamat?: number }} [options]
 */
export const claimReward = async (referralLinkId, conditionIndex, options = {}) => {
  const url = `${USER_API_URL}/referral/claim`;
  const body = { referral_link_id: referralLinkId };
  if (conditionIndex !== undefined && conditionIndex !== null) {
    body.condition_index = conditionIndex;
  }
  const aid = options.idAlamat;
  if (aid !== undefined && aid !== null && Number(aid) > 0) {
    body.id_alamat = Number(aid);
  }
  const res = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (res?.status !== 'success') throw new Error(res?.message || 'Failed to claim reward');
  return res?.data ?? null;
};
